import React, { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios, { fehlerText } from '../api/http';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PasswordIcon from '@mui/icons-material/Password';

// Kontext
import { AuthContext } from '../context/AuthContext';
import BestaetigungsDialog from '../components/common/BestaetigungsDialog';

// Benutzer-Typ
type User = {
  id: number;
  name: string;
  email: string;
  role: string;
  active: number;
};

// Rollenfarben-Mapping
const getRoleColor = (role: string): "primary" | "secondary" | "success" | "info" | "warning" | "error" | "default" => {
  switch (role) {
    case 'Admin':
      return 'error'; // Rot für Administratoren
    case 'Projektleiter':
      return 'warning'; // Orange für Projektleiter
    case 'Mitarbeiter':
      return 'info'; // Blau für Mitarbeiter
    default:
      return 'default'; // Grau für unbekannte Rollen
  }
};

/**
 * Passwortregeln – identisch zur Prüfung im Backend (server/src/utils/validation.ts).
 * Vorher verlangte das Formular nur 6 Zeichen, der Server lehnte solche
 * Passwörter aber ab.
 */
const passwortRegel = Yup.string()
  .min(10, 'Das Passwort muss mindestens 10 Zeichen lang sein')
  .matches(/[A-Za-z]/, 'Das Passwort muss mindestens einen Buchstaben enthalten')
  .matches(/[0-9]/, 'Das Passwort muss mindestens eine Zahl enthalten');

// Form-Schema für Benutzer
const userSchema = Yup.object({
  name: Yup.string().min(2, 'Der Name muss mindestens 2 Zeichen lang sein').required('Name ist erforderlich'),
  email: Yup.string().email('Ungültige E-Mail-Adresse').required('E-Mail ist erforderlich'),
  role: Yup.string()
    .oneOf(['Admin', 'Projektleiter', 'Mitarbeiter'], 'Bitte eine gültige Rolle wählen')
    .required('Rolle ist erforderlich'),
  password: Yup.string().when('isNew', {
    is: (isNew: boolean) => isNew === true,
    then: () => passwortRegel.required('Passwort ist erforderlich'),
    otherwise: () => passwortRegel
  }),
  isNew: Yup.boolean()
});

// Passwort-Reset-Schema
const passwordResetSchema = Yup.object({
  newPassword: passwortRegel.required('Neues Passwort ist erforderlich'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwörter müssen übereinstimmen')
    .required('Passwortbestätigung ist erforderlich')
});

const Users = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog-States
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [zuSperren, setZuSperren] = useState<User | null>(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);

  // Formik für den Benutzer-Dialog
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      role: 'Mitarbeiter',
      password: '',
      isNew: true
    },
    validationSchema: userSchema,
    onSubmit: async (values) => {
      try {
        setError(null);
        
        if (isNewUser) {
          // Neuen Benutzer erstellen
          await axios.post('/api/users', {
            name: values.name,
            email: values.email,
            password: values.password,
            role: values.role
          });
          setSuccess('Benutzer erfolgreich erstellt');
        } else if (selectedUser) {
          // Benutzer aktualisieren
          await axios.put(`/api/users/${selectedUser.id}`, {
            name: values.name,
            email: values.email,
            role: values.role,
            active: selectedUser.active
          });
          setSuccess('Benutzer erfolgreich aktualisiert');
        }
        
        // Dialog schließen und Benutzerliste neu laden
        setOpenUserDialog(false);
        loadUsers();
      } catch (err: any) {
        console.error('Fehler:', err);
        setError(err.response?.data?.message || 'Ein unerwarteter Fehler ist aufgetreten');
      }
    }
  });

  // Formik für den Passwort-Reset-Dialog
  const passwordFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordResetSchema,
    onSubmit: async (values) => {
      try {
        setError(null);
        
        if (selectedUser) {
          // Passwort zurücksetzen
          await axios.patch(`/api/users/${selectedUser.id}/reset-password`, {
            newPassword: values.newPassword
          });
          setSuccess('Passwort erfolgreich zurückgesetzt');
        }
        
        // Dialog schließen
        setOpenPasswordDialog(false);
        passwordFormik.resetForm();
      } catch (err: any) {
        console.error('Fehler beim Zurücksetzen des Passworts:', err);
        setError(err.response?.data?.message || 'Ein unerwarteter Fehler ist aufgetreten');
      }
    }
  });

  // Benutzer laden
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/users');
      setUsers(response.data.users);
    } catch (err: any) {
      console.error('Fehler beim Laden der Benutzer:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  // Benutzer sperren/entsperren
  // Sperren bzw. Entsperren wird erst nach Bestätigung ausgeführt: ein
  // versehentlicher Klick hat den Benutzer sofort ausgeschlossen.
  const toggleUserActive = async () => {
    if (!zuSperren) return;

    try {
      setError(null);

      const response = await axios.patch(`/api/users/${zuSperren.id}/toggle-active`);
      setSuccess(response.data.message);
      setZuSperren(null);

      // Benutzerliste aktualisieren
      loadUsers();
    } catch (err) {
      setError(fehlerText(err));
      setZuSperren(null);
    }
  };

  // Benutzer löschen
  const deleteUser = async () => {
    try {
      if (!selectedUser) return;
      
      setError(null);
      await axios.delete(`/api/users/${selectedUser.id}`);
      setSuccess('Benutzer erfolgreich gelöscht');
      
      // Dialog schließen und Benutzerliste neu laden
      setOpenDeleteDialog(false);
      loadUsers();
    } catch (err: any) {
      console.error('Fehler beim Löschen des Benutzers:', err);
      setError(err.response?.data?.message || 'Ein unerwarteter Fehler ist aufgetreten');
    }
  };

  // Dialog zum Erstellen eines neuen Benutzers öffnen
  const handleOpenNewUserDialog = () => {
    setIsNewUser(true);
    setSelectedUser(null);
    formik.resetForm({
      values: {
        name: '',
        email: '',
        role: 'Mitarbeiter',
        password: '',
        isNew: true
      }
    });
    setOpenUserDialog(true);
  };

  // Dialog zum Bearbeiten eines Benutzers öffnen
  const handleOpenEditUserDialog = (user: User) => {
    setIsNewUser(false);
    setSelectedUser(user);
    formik.resetForm({
      values: {
        name: user.name,
        email: user.email,
        role: user.role,
        password: '',
        isNew: false
      }
    });
    setOpenUserDialog(true);
  };

  // Dialog zum Löschen eines Benutzers öffnen
  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  // Dialog zum Zurücksetzen des Passworts öffnen
  const handleOpenPasswordDialog = (user: User) => {
    setSelectedUser(user);
    passwordFormik.resetForm();
    setOpenPasswordDialog(true);
  };

  // Benutzer beim ersten Laden abrufen
  useEffect(() => {
    loadUsers();
  }, []);

  // Erfolgs- und Fehlermeldungen nach 5 Sekunden ausblenden
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Schauen, ob der aktuelle Benutzer ein Admin ist
  const isAdminUser = user?.role === 'Admin';
  
  if (!isAdminUser) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Zugriff verweigert
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Sie haben keine Berechtigung, auf die Benutzerverwaltung zuzugreifen.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3 
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNewUserDialog}
          fullWidth={isMobile}
        >
          Neuer Benutzer
        </Button>
      </Box>

      {/* Fehlermeldung */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Erfolgsmeldung */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Ladeindikator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Alert severity="info">
          Keine Benutzer gefunden.
        </Alert>
      ) : (
        <>
          {isMobile ? (
            // Mobile Ansicht: Karten
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {users.map((user) => (
                <Paper key={user.id} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Chip 
                      label={user.role} 
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Tooltip title="Passwort zurücksetzen">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPasswordDialog(user)}
                        color="primary"
                        aria-label={`Passwort von ${user.name} zurücksetzen`}
                      >
                        <PasswordIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.active ? 'Benutzer sperren' : 'Benutzer entsperren'}>
                      <IconButton
                        size="small"
                        onClick={() => setZuSperren(user)}
                        color={user.active ? 'error' : 'success'}
                        aria-label={`${user.name} ${user.active ? 'sperren' : 'entsperren'}`}
                      >
                        {user.active ? <LockIcon /> : <LockOpenIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Benutzer bearbeiten">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditUserDialog(user)}
                        color="primary"
                        aria-label={`${user.name} bearbeiten`}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Benutzer löschen">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(user)}
                        color="error"
                        aria-label={`${user.name} löschen`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            // Desktop Ansicht: Tabelle
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Rolle</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.role} 
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.active ? "Aktiv" : "Gesperrt"}
                          color={user.active ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="Passwort zurücksetzen">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPasswordDialog(user)}
                              color="primary"
                              aria-label={`Passwort von ${user.name} zurücksetzen`}
                            >
                              <PasswordIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.active ? 'Benutzer sperren' : 'Benutzer entsperren'}>
                            <IconButton
                              size="small"
                              onClick={() => setZuSperren(user)}
                              color={user.active ? 'error' : 'success'}
                              aria-label={`${user.name} ${user.active ? 'sperren' : 'entsperren'}`}
                            >
                              {user.active ? <LockIcon /> : <LockOpenIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Benutzer bearbeiten">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditUserDialog(user)}
                              color="primary"
                              aria-label={`${user.name} bearbeiten`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Benutzer löschen">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(user)}
                              color="error"
                              aria-label={`${user.name} löschen`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Dialog für Benutzer erstellen/bearbeiten */}
      <Dialog 
        open={openUserDialog} 
        onClose={() => setOpenUserDialog(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {isNewUser ? 'Neuen Benutzer erstellen' : 'Benutzer bearbeiten'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
              <TextField
                fullWidth
                id="email"
                name="email"
                label="E-Mail"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
              />
              <FormControl fullWidth>
                <InputLabel id="role-label">Rolle</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  label="Rolle"
                  required
                >
                  <MenuItem value="Admin">Administrator</MenuItem>
                  <MenuItem value="Projektleiter">Projektleiter</MenuItem>
                  <MenuItem value="Mitarbeiter">Mitarbeiter</MenuItem>
                </Select>
              </FormControl>
              {isNewUser && (
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Passwort"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  required={isNewUser}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setOpenUserDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {isNewUser ? 'Erstellen' : 'Speichern'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog für Passwort zurücksetzen */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => setOpenPasswordDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={passwordFormik.handleSubmit}>
          <DialogTitle>
            Passwort zurücksetzen
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                id="newPassword"
                name="newPassword"
                label="Neues Passwort"
                type="password"
                value={passwordFormik.values.newPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                required
              />
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Passwort bestätigen"
                type="password"
                value={passwordFormik.values.confirmPassword}
                onChange={passwordFormik.handleChange}
                error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setOpenPasswordDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={passwordFormik.isSubmitting}
            >
              Zurücksetzen
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog für Benutzer löschen */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Benutzer löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Benutzer "{selectedUser?.name}" wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Abbrechen
          </Button>
          <Button 
            onClick={deleteUser}
            variant="contained" 
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sperren/Entsperren bestätigen */}
      <BestaetigungsDialog
        offen={Boolean(zuSperren)}
        titel={zuSperren?.active ? 'Benutzer sperren?' : 'Benutzer entsperren?'}
        text={
          zuSperren?.active
            ? `"${zuSperren?.name}" kann sich danach nicht mehr anmelden. Bereits erfasste Zeiten bleiben erhalten.`
            : `"${zuSperren?.name}" kann sich danach wieder anmelden.`
        }
        bestaetigenText={zuSperren?.active ? 'Sperren' : 'Entsperren'}
        gefaehrlich={Boolean(zuSperren?.active)}
        onBestaetigen={toggleUserActive}
        onAbbrechen={() => setZuSperren(null)}
      />
    </Box>
  );
};

export default Users; 